import { CSSProperties, ReactNode, useCallback, useContext, useMemo } from "react";
import { Dropdown, MenuItem, Nav, Navbar, NavItem } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import Announcements from "../Announcements/Announcements";
import { SessionContext } from "../common/contexts/SessionContext";
import { useFeatureFlag } from "../common/contexts/UseFeatureFlags";
import { LucideIcon } from "../common/icon/LucideIcon";
import { Documentation } from "../Documentation/Documentation";
import styles from "./main.module.css";
import { getDetailLink, getEditLink, getIndexRoute } from "./Routing";
import { projectsConstants } from "../api/Projects";
import { samplesConstants } from "../api/Samples";
import { datasetsConstants } from "../api/Datasets";
import { inventoriesConstants } from "../api/Inventories";
import { personsConstants } from "../api/Person";
import { documentationConstants, logsPyConstants, logsSupportConstants } from "../api/Misc";
import { toUppercase } from "../common/helperfunctions/stringFunctions";
import { useAuth } from "react-oidc-context";
import { FileExportJobsWidget } from "../FileExportJobs/FileExportJobsWidget";
import { dismissToast } from "../common/overlays/Toasts/showtoast";
import { genericEntityConstants } from "../api/GenericConstants";
import { ZontalIcon } from "../Integrations/ZontalCommons";

export const NavigationBar = () => {
  const history = useHistory();
  const { group, session } = useContext(SessionContext);
  const route = useMemo(() => (url: string) => `/${group}${url}`, [group]);
  const logout = useLogout();
  const isElnActivated = useFeatureFlag("eln");
  const isZontalActivated = useFeatureFlag("enable_zontal_integration");

  return (
    <Navbar className="navbar navbar-default navbar-fixed-top" componentClass="div" fluid fixedTop>
      <Navbar.Header>
        <Navbar.Brand style={{ position: "relative", width: "min-content", height: "58px", overflow: "hidden" }}>
          <a className="navbar-brand" href={route(getIndexRoute("dashboard"))}>
            <img src={`${process.env.PUBLIC_URL}/assets/logos/Logs-Main.svg`} className="brand" alt="LOGS" />
          </a>
        </Navbar.Brand>
        {(process.env.NODE_ENV === "development" || session?.features.development_mode) && (
          <div
            className="flex row-nowrap align-center gap-5"
            style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
          >
            <label className="label label-xs label-danger" style={{ margin: 0, fontSize: ".6em" }}>
              DEVEL
            </label>
          </div>
        )}
        <Navbar.Toggle />
      </Navbar.Header>
      <Navbar.Collapse>
        <Nav>
          <NavBarMenuItem to={route(getIndexRoute(projectsConstants.frontendIndexRoute))}>Projects</NavBarMenuItem>
          <NavBarMenuItem to={route(getIndexRoute(samplesConstants.frontendIndexRoute))}>Samples</NavBarMenuItem>
          {/* <NavBarMenuItem to={route("/samples_old")}>Samples old</NavBarMenuItem> */}
          <NavBarMenuItem to={route(getIndexRoute(datasetsConstants.frontendIndexRoute))}>Datasets</NavBarMenuItem>
          <NavBarMenuItem to={route(getIndexRoute(inventoriesConstants.frontendIndexRoute))}>
            Inventories
          </NavBarMenuItem>
          {session?.features.development_mode && (
            <NavBarMenuItem to={route(getIndexRoute("components"))}>Components</NavBarMenuItem>
          )}
          {isElnActivated && <NavBarMenuItem to={route(getIndexRoute("notebooks"))}>Notebook</NavBarMenuItem>}
          <NavBarMenuItem to={route(getIndexRoute("manage"))}>More</NavBarMenuItem>

          {isZontalActivated && (
            <NavBarMenuItem
              to={`${route(getIndexRoute(genericEntityConstants.zontalIPP.entityConstants.frontendIndexRoute))}?fullscreen=true`}
              liStyle={{
                borderLeft: "1px solid var(--gray-400)",
                // borderRight: "1px solid var(--gray-400)",
              }}
            >
              <ZontalIcon style={{ width: "20px", height: "20px", verticalAlign: "text-bottom", marginRight: "4px" }} />
            </NavBarMenuItem>
          )}
        </Nav>
        <Nav pullRight>
          <NavItem className="navWidget">
            <FileExportJobsWidget />
          </NavItem>
          <NavItem className="navWidget">
            <Announcements />
          </NavItem>
          <NavItem className="navWidget">
            <Dropdown id="help" pullRight={true}>
              <Dropdown.Toggle noCaret className={"btn-ghost-secondary btn-round"} bsStyle="link">
                <Documentation />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <MenuItem
                  href={"https://logs-support.sciy.com/home/"}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <LucideIcon name={documentationConstants.icon} /> {toUppercase(documentationConstants.entitySingular)}
                </MenuItem>
                <MenuItem
                  href={"https://docs.logs-python.com"}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/logos/LOGS-Py_Icon.svg`}
                    className="feather"
                    alt={toUppercase(logsPyConstants.entitySingular)}
                  />{" "}
                  {toUppercase(logsPyConstants.entitySingular)}
                </MenuItem>
                <MenuItem
                  href={"https://logs-repository.atlassian.net/servicedesk/customer/portal/4"}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <LucideIcon name={"mail-question"} /> New data format request
                </MenuItem>

                <MenuItem
                  href={"https://logs-support.sciy.com/new/"}
                  target="_blank"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <LucideIcon name={logsSupportConstants.icon} /> {toUppercase(logsSupportConstants.entitySingular)}
                </MenuItem>
                <MenuItem
                  href={route(getIndexRoute("about"))}
                  onClick={(e) => {
                    e.preventDefault();
                    history.push(route(getIndexRoute("about")));
                  }}
                >
                  <LucideIcon name="info" /> About LOGS
                </MenuItem>
              </Dropdown.Menu>
            </Dropdown>
          </NavItem>
          <Dropdown id="account" className={styles.dropdown} pullRight={true}>
            <Dropdown.Toggle className="btn dropdown-toggle user-account">
              <span>
                {/* <img src="assets/img/user-badge-admin.svg" className="user-role" /> */}
                <LucideIcon name="check" className="feather user-role" color={"var(--success)"} />
                <LucideIcon name="user" className="feather feather-icon" /> {session?.person?.lastName}
              </span>{" "}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <li className="dropdown-header">
                <strong>{`${session?.person?.firstName ?? ""} ${session?.person?.lastName ?? ""}`}</strong>
              </li>
              <li className="divider"></li>
              <MenuItem
                disabled={!session?.userId}
                href={route(getDetailLink(personsConstants.frontendIndexRoute, session?.userId || 0))}
                onClick={(e) => {
                  e.preventDefault();
                  history.push(route(getDetailLink(personsConstants.frontendIndexRoute, session?.userId || 0)));
                }}
              >
                <LucideIcon name="eye" /> View account
              </MenuItem>
              <MenuItem
                disabled={!session?.userId}
                href={route(getEditLink(personsConstants.frontendIndexRoute, session?.userId || 0))}
                onClick={(e) => {
                  e.preventDefault();
                  history.push(route(getEditLink(personsConstants.frontendIndexRoute, session?.userId || 0)));
                }}
              >
                <LucideIcon name="square-pen" /> Edit account
              </MenuItem>
              <MenuItem onClick={logout}>
                <LucideIcon name="log-out" /> Log out
              </MenuItem>
            </Dropdown.Menu>
          </Dropdown>
        </Nav>
      </Navbar.Collapse>
    </Navbar>
  );
};

const NavBarMenuItem = ({ to, children, liStyle }: { to: string; children: ReactNode; liStyle?: CSSProperties }) => {
  const location = useLocation();
  const isFocused = location.pathname.split("/").pop() === to.split("/").pop();

  return (
    <li className={`${isFocused ? "active" : ""}`} style={liStyle}>
      <NavLink to={to} className={`${styles.navlink}`} activeClassName={`${styles.active}`}>
        {children}
      </NavLink>
    </li>
  );
};

// For some reason this breaks the dropdown behaviour, don't use this for now
// const NavBarDropdownMenuItem = ({
//   to,
//   closeDropdown,
//   children,
// }: {
//   to: string;
//   closeDropdown: (x: any) => void;
//   children: ReactNode;
// }) => (
//   <li role="presentation">
//     <NavLink role="menuitem" to={to} activeClassName={styles.active} onClick={closeDropdown}>
//       <span>{children}</span>
//     </NavLink>
//   </li>
// );

export const useLogout = () => {
  const oidcAuth = useAuth();
  const context = useContext(SessionContext);
  const queryClient = useQueryClient();

  const internalLogoutFunc = useCallback(() => {
    // console.log("LOGOUT");
    sessionStorage.removeItem(`session_${context.group}`);
    localStorage.removeItem(`session_${context.group}`);
    context.session = undefined;
    // queryClient.removeQueries(["session"]);
    queryClient.removeQueries();
    context.clearSession();
    dismissToast();
  }, [context, queryClient]);

  const oidcLogoutFunc = useCallback(async () => {
    // console.log("Removing user");
    await oidcAuth.removeUser();
    context.session = undefined;
    // queryClient.removeQueries(["session"]);
    queryClient.removeQueries();
    context.clearSession();
    // console.log("User removed");
    dismissToast();
  }, [context, oidcAuth, queryClient]);

  if (context.session?.type === "jwt") return oidcLogoutFunc;
  else return internalLogoutFunc;
};
